import { Loader2, Chrome, Building2 } from "lucide-react";

interface OAuthHandlers {
  onGoogle: () => void;
  onGithub: () => void;
  onMicrosoft: () => void;
  onSSO: () => void;
}

interface OAuthLoadingStates {
  google?: boolean;
  github?: boolean;
  microsoft?: boolean;
  sso?: boolean;
  any?: boolean;
}

interface OAuthButtonRowProps extends OAuthHandlers {
  loading?: OAuthLoadingStates;
  ssoProviderName?: string | null;
  verb?: "sign in" | "continue" | "sign up";
  /**
   * `compact` (default) renders Google/GitHub/Microsoft as an
   * icon-only horizontal row + a single labelled SSO button below.
   * Saves ~90px of vertical space vs the legacy stacked layout —
   * keeps the right column balanced against the left credential
   * form even with the Passkey CTA on top.
   */
  compact?: boolean;
}

const GoogleIcon = () => <Chrome className="w-4 h-4" aria-hidden="true" />;

const GithubIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
  </svg>
);

const MicrosoftIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 23 23" fill="currentColor" aria-hidden="true">
    <rect x="1" y="1" width="10" height="10" />
    <rect x="12" y="1" width="10" height="10" />
    <rect x="1" y="12" width="10" height="10" />
    <rect x="12" y="12" width="10" height="10" />
  </svg>
);

const SSOIcon = () => <Building2 className="w-4 h-4" aria-hidden="true" />;

interface SingleOAuthProps {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

function SingleOAuth({ label, icon, onClick, loading, disabled }: SingleOAuthProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className="adp-btn w-full inline-flex items-center justify-center gap-2.5 min-h-[44px]"
    >
      <span className="inline-flex items-center text-[var(--muted-foreground)]">
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin motion-reduce:hidden" aria-hidden="true" />
        ) : (
          icon
        )}
      </span>
      <span>{label}</span>
    </button>
  );
}

/**
 * OAuthButtonRow — alternative sign-in methods.
 *
 * `compact` (default): Google/GitHub/Microsoft as an icon-only
 * horizontal row sharing equal width, plus a single labelled SSO
 * button below. Saves ~90px vertical vs the legacy stacked layout
 * so the right column stays balanced against the left credential
 * form when a Passkey CTA sits above.
 *
 * `compact={false}`: legacy four-stacked layout retained for any
 * surface (e.g. SignUpPage) that prefers the verbose presentation.
 *
 * When `ssoProviderName` is provided (domain probe matched), the
 * SSO button relabels to "Sign in with Okta" etc. Domain mismatch
 * is enforced server-side after the round-trip, never client-side.
 */
interface CompactOAuthIconProps {
  ariaLabel: string;
  icon: React.ReactNode;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

function CompactOAuthIcon({ ariaLabel, icon, onClick, loading, disabled }: CompactOAuthIconProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-label={ariaLabel}
      title={ariaLabel}
      className="adp-btn inline-flex items-center justify-center min-h-[44px] flex-1"
    >
      <span className="inline-flex items-center text-[var(--muted-foreground)]">
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin motion-reduce:hidden" aria-hidden="true" />
        ) : (
          icon
        )}
      </span>
    </button>
  );
}

export function OAuthButtonRow({
  onGoogle,
  onGithub,
  onMicrosoft,
  onSSO,
  loading = {},
  ssoProviderName = null,
  verb = "sign in",
  compact = false,
}: OAuthButtonRowProps) {
  const verbLabel =
    verb === "sign up" ? "Sign up with" : verb === "continue" ? "Continue with" : "Sign in with";

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-stretch gap-2" role="group" aria-label="Social sign-in">
          <CompactOAuthIcon
            ariaLabel={`${verbLabel} Google`}
            icon={<GoogleIcon />}
            onClick={onGoogle}
            loading={loading.google}
            disabled={loading.any}
          />
          <CompactOAuthIcon
            ariaLabel={`${verbLabel} GitHub`}
            icon={<GithubIcon />}
            onClick={onGithub}
            loading={loading.github}
            disabled={loading.any}
          />
          <CompactOAuthIcon
            ariaLabel={`${verbLabel} Microsoft`}
            icon={<MicrosoftIcon />}
            onClick={onMicrosoft}
            loading={loading.microsoft}
            disabled={loading.any}
          />
        </div>
        <SingleOAuth
          label={ssoProviderName ? `${verbLabel} ${ssoProviderName}` : "Single sign-on (SSO)"}
          icon={<SSOIcon />}
          onClick={onSSO}
          loading={loading.sso}
          disabled={loading.any}
        />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <SingleOAuth
        label={`${verbLabel} Google`}
        icon={<GoogleIcon />}
        onClick={onGoogle}
        loading={loading.google}
        disabled={loading.any}
      />
      <SingleOAuth
        label={`${verbLabel} GitHub`}
        icon={<GithubIcon />}
        onClick={onGithub}
        loading={loading.github}
        disabled={loading.any}
      />
      <SingleOAuth
        label={`${verbLabel} Microsoft`}
        icon={<MicrosoftIcon />}
        onClick={onMicrosoft}
        loading={loading.microsoft}
        disabled={loading.any}
      />
      <SingleOAuth
        label={ssoProviderName ? `${verbLabel} ${ssoProviderName}` : "Single sign-on (SSO)"}
        icon={<SSOIcon />}
        onClick={onSSO}
        loading={loading.sso}
        disabled={loading.any}
      />
    </div>
  );
}
